import { useEffect, useState } from "react";
import TablaProyectosVista from "../vista/tablaProyectosVista";
import { UbicacionModelo } from "../../../modelos/Ubicacion";
import type { IPais } from "../../../modelos/Ubicacion/entidades/IPais";
import type { IProyecto } from "../../../modelos/Proyectos/entidades/IProyecto";
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type SortingState } from "@tanstack/react-table";
import { columnasTablaProyecto } from "../vista/constantesTablaProyectos";
import { estadoInicialFiltroProyecto } from "../interfaces";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { useConfirmacion } from "../../../globales/contextos/confirmacionContext";
import { useProyecto } from "../contextoProyecto";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import ModalproyectoCV from "./modalProyectoCV";
import type { IFiltroProyecto } from "../../../modelos/Proyectos/entidades/IFiltroProyecto";

const ProyectosCV = () => {
  const modeloProyecto = new ProyectoModelo();
  const modeloUbicacion = new UbicacionModelo();

  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const { manejarCargando } = useCargando();

  const PAGE_SIZE = 15;
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [listaProyectos, setListaProyectos] = useState<IProyecto[]>([]);
  const [listaProyectosCompleta, setListaProyectosCompleta] = useState<IProyecto[]>([]);
  const [listaPaises, setListaPaises] = useState<IPais[]>([]);
  const [habilitarFiltros, setHabilitarFiltros] = useState<boolean>(false);
  const [filtroProyecto, setFiltroProyecto] = useState<IFiltroProyecto>(estadoInicialFiltroProyecto);

  const { setProyecto } = useProyecto();
  const [abrirModal, setAbrirModal] = useState(false);

  useEffect(() => {
    consultarProyectos(estadoInicialFiltroProyecto);
  }, []);

  const consultarProyectos = async (filtro: IFiltroProyecto) => {
    try {
      manejarCargando(true, "Consultando proyectos...", false);
      
      const [proyectos, paisesRespuesta] = await Promise.all([
        modeloProyecto.obtenerListaProyectos(filtro.NitProyecto),
        modeloUbicacion.obtenerPaises()
      ]);
      
      if(paisesRespuesta.Data) {
        setListaPaises(paisesRespuesta.Data);
      }

      const proyectosFiltrados = proyectos.Data.filter((p: IProyecto) => !p.Eliminado);
      
      // Guardar la lista completa
      setListaProyectosCompleta(proyectosFiltrados);
      
      // Aplicar filtros en el frontend
      let proyectosFiltradosFront = proyectosFiltrados;
      
      // Filtrar por ID de proyecto si existe
      if (filtro.ProyectoId) {
        proyectosFiltradosFront = proyectosFiltradosFront.filter((p: IProyecto) => 
          p.Id === filtro.ProyectoId
        );
      }
      
      // Filtrar por NIT si existe
      if (filtro.NitProyecto && filtro.NitProyecto.trim() !== '') {
        proyectosFiltradosFront = proyectosFiltradosFront.filter((p: IProyecto) => 
          p.NitProyecto.toLowerCase().includes(filtro.NitProyecto!.toLowerCase())
        );
      }

      // Filtrar por País si existe
      if (filtro.PaisId) {
        proyectosFiltradosFront = proyectosFiltradosFront.filter((p: IProyecto) => 
          p.PaisId === filtro.PaisId
        );
      }
      
      setListaProyectos(proyectosFiltradosFront);

    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar proyectos',
        'No fue posible consultar los proyectos. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    }
    finally {
      manejarCargando(false);
    }
  }

  const manejarHabilitarFiltros = (habilitar: boolean) => {
    setHabilitarFiltros(habilitar)
  }

  const manejarCambioFiltroProyecto = async (campo: keyof IFiltroProyecto, valor: IFiltroProyecto[keyof IFiltroProyecto]) => {
    setFiltroProyecto((prevFiltroProyecto) => ({
      ...prevFiltroProyecto,
      [campo]: valor
    }));
  }

  const eliminarProyecto = async (proyecto: IProyecto) => {
    const confirmado = await confirmar({
      titulo: `Eliminar proyecto`,
      mensaje: `¿Está seguro de que desea eliminar el proyecto ${proyecto.Nombre}? Esta acción no se puede deshacer.`,
      textoAceptar: "Sí, eliminar",
      textoCancelar: "Cancelar",
      tipo: EnumTipoNotificacion.Error,
      opcionCancelar: true
    });
    if (confirmado) {
      try {
        manejarCargando(true);
        const proyectoRespuesta = await modeloProyecto.eliminarProyecto(proyecto);
        if (proyectoRespuesta.NotificationType === EnumTipoNotificacion.Success) {
          consultarProyectos(estadoInicialFiltroProyecto)
        }
        if (proyectoRespuesta.Messages.length > 0) {
          proyectoRespuesta.Messages.forEach(mensaje =>
            mostrarNotificacion(
              proyectoRespuesta.NotificationType,
              '',
              mensaje,
              4000
            ));
        }

      }
      catch {
        mostrarNotificacion(
          EnumTipoNotificacion.Error,
          'Error al eliminar proyecto',
          'No fue posible eliminar el proyecto. Por favor verifica tu conexión e intenta nuevamente.',
          5000
        );
      } finally {
        manejarCargando(false);
      }
    }
  }


  const tabla = useReactTable({
    data: listaProyectos,
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex, pageSize: PAGE_SIZE }, sorting },
    onPaginationChange: updater => {
      if (typeof updater === 'function') {
        const newState = updater({ pageIndex, pageSize: PAGE_SIZE });
        setPageIndex(newState.pageIndex);
      } else {
        setPageIndex(updater.pageIndex ?? 0);
      }
    },
    pageCount: Math.ceil(listaProyectos.length / PAGE_SIZE),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
  });

  const columns = columnasTablaProyecto({
    manejarEdicionProyecto: (proyecto: IProyecto) => abrirModalProyecto(true, proyecto),
    manejarEliminacionProyecto: (proyecto) => eliminarProyecto(proyecto)
  });

  tabla.setOptions((prev) => ({ ...prev, columns }));

  const abrirModalProyecto = (estadoModal: boolean, proyecto: IProyecto | null) => {
    if (proyecto) {
      setProyecto({ ...proyecto, RegionalId: proyecto.Regional?.Id || 0, ZonaId: proyecto.Zona?.Id || 0 });
    }
    if (!estadoModal) {
      limpiarFiltros();
    }
    setAbrirModal(estadoModal);
  };

  const limpiarFiltros = () => {
    setFiltroProyecto(estadoInicialFiltroProyecto);
    consultarProyectos(estadoInicialFiltroProyecto)
  }

  return (
    <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TablaProyectosVista
          abrirModalProyecto={abrirModalProyecto}
          tabla={tabla}
          consultarProyectos={consultarProyectos}
          filtro={filtroProyecto}
          habilitarFiltros={habilitarFiltros}
          manejarCambioFiltroProyecto={manejarCambioFiltroProyecto}
          manejarHabilitarFiltros={manejarHabilitarFiltros}
          limpiarFiltros={limpiarFiltros}
          listaProyectos={listaProyectosCompleta}
          listaPaises={listaPaises}
        />
      </div>
      <ModalproyectoCV
        consultarProyectos={consultarProyectos}
        abrirModalProyecto={abrirModalProyecto}
        estadoModal={abrirModal}
      />
    </>
  )
}

export default ProyectosCV;